import React from 'react'
import { useState, useEffect } from 'react'
import { Github, Linkedin, Mail } from 'lucide-react'
import { FaFacebook, FaInstagram, FaTiktok, FaYoutube } from 'react-icons/fa'

import Hero from './components/Hero'
import About from './components/About'
import Education from './components/Education'
import Projects from './components/Projects'
import Resume from './components/Resume'
import Testimonials from './components/Testimonials'
import Contact from './components/Contact'
import Footer from './components/Footer'
import ScrollToTop from './components/ScrollToTop'
import Sidebar from './components/Sidebar'
import { useDarkMode } from './hooks/useDarkMode'
import { useScrollAnimation } from './hooks/useScrollAnimation'

function App() {
  const [darkMode, setDarkMode] = useDarkMode()
  useScrollAnimation()

  const [scrollY, setScrollY] = useState(0)
  const [showSocialBar, setShowSocialBar] = useState(true)

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY)
      
      // Hide social bar when reaching footer section
      const footer = document.querySelector('footer')
      if (footer) {
        const footerTop = footer.offsetTop
        const windowHeight = window.innerHeight
        const currentScroll = window.scrollY + windowHeight
        
        // Hide when reaching footer
        if (currentScroll >= footerTop + 100) {
          setShowSocialBar(false)
        } else {
          setShowSocialBar(true)
        }
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const socialLinks = [
    { icon: Github, href: 'https://github.com/Shehan2004-web', label: 'GitHub', color: 'hover:text-gray-900 dark:hover:text-white' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/shehan-chamika-334562383', label: 'LinkedIn', color: 'hover:text-blue-600' },
    { icon: FaFacebook, href: 'https://www.facebook.com/shehan.chamika.33671', label: 'Facebook', color: 'hover:text-blue-500' },
    { icon: FaInstagram, href: 'https://www.instagram.com/shehanscp', label: 'Instagram', color: 'hover:text-pink-600' },
    { icon: FaTiktok, href: 'https://www.tiktok.com/@chamiya_scp', label: 'TikTok', color: 'hover:text-black dark:hover:text-white' },
    { icon: FaYoutube, href: 'https://youtube.com/@codemaster-l5e', label: 'YouTube', color: 'hover:text-red-600' },
    { icon: Mail, href: 'mailto:chamikascp@gmail.com', label: 'Email', color: 'hover:text-green-600' },
  ]

  return (
    <div className={darkMode ? 'dark' : ''}>
      <div className="min-h-screen bg-white dark:bg-github-bg transition-colors duration-300 relative pl-0 sm:pl-20 pb-20 sm:pb-0">
        <Sidebar darkMode={darkMode} setDarkMode={setDarkMode} />
        
        {/* Fixed Social Media Bar - Visible on all pages except footer */}
        <div 
          className={`hidden lg:flex fixed right-8 top-1/2 -translate-y-1/2 z-[100] flex-col gap-4 transition-all duration-500 ${showSocialBar ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20 pointer-events-none'}`}
        >
          {socialLinks.map((social, index) => {
            const Icon = social.icon
            return (
              <a
                key={social.label}
                href={social.href}
                target={social.href.startsWith('http') ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className={`group relative flex items-center justify-center w-12 h-12 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-full shadow-lg ${social.color} text-slate-600 dark:text-slate-300 transition-all duration-300 hover:scale-110 hover:shadow-2xl border border-slate-200 dark:border-slate-700`}
                style={{
                  animation: `slideInRight 0.6s ease-out ${index * 0.1}s both`,
                }}
                aria-label={social.label}
              >
                {/* Icon */}
                <Icon size={20} className="relative z-10" />
                
                {/* Tooltip - Appears on left side */}
                <span className="absolute right-full mr-3 px-3 py-1.5 bg-slate-900 dark:bg-slate-700 text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap pointer-events-none shadow-xl">
                  {social.label}
                  <span className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-slate-900 dark:border-l-slate-700"></span>
                </span>

                {/* Hover Glow Effect */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-20 blur-md transition-opacity duration-300"></div>
              </a>
            )
          })}
        </div>

        {/* Mobile Social Media Bar - Bottom Fixed (Visible only on Mobile, hidden on footer) */}
        <div className={`lg:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] flex gap-3 px-6 py-3 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-full shadow-2xl border border-slate-200 dark:border-slate-700 transition-all duration-500 ${showSocialBar ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20 pointer-events-none'}`}>
          {socialLinks.map((social, index) => {
            const Icon = social.icon
            return (
              <a
                key={social.label}
                href={social.href}
                target={social.href.startsWith('http') ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className={`flex items-center justify-center w-10 h-10 bg-white dark:bg-slate-800 rounded-full shadow-md ${social.color} text-slate-600 dark:text-slate-300 transition-all duration-300 hover:scale-110 active:scale-95 border border-slate-200 dark:border-slate-700`}
                aria-label={social.label}
              >
                <Icon size={18} />
              </a>
            )
          })}
        </div>

        <main className="relative z-10 pb-20 md:pb-0 transition-all duration-500">
          <Hero />
          <About />
          <Education />
          <Projects />
          {/* Blog section removed by request */}
          <Resume />
          <Testimonials />
          <Contact />
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </div>
  )
}

export default App